import { Dialog, DialogProvider, useDialogContext } from '@ariakit/react'
import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'

import {
  Exclamation,
  IconButton,
  ResponsiveImage,
  ScreenReaderOnly,
  SelectMark,
  TextLink,
  TypeStyleBodySmall,
  TypeStyleHeadlineExtraExtraSmall,
  timingFast,
  useIntersection,
} from '@syconium/little-miss-figgy'
import { fromMd } from '@syconium/little-miss-figgy'

import { NavContainer } from '../../../brunswick/containers/NavContainer'
import { promoBarHeightDesktop, promoBarHeightMobile } from '../../../constants'
import { ToastNotificationEvent } from '../../../types/toasts'
import { useCustomEventListener } from '../../_hooks/useCustomEventListener'
import { useNavHeader } from '../../_providers/NavHeaderProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { NextLink } from '../navigation/NextLink'

const FavoritesErrorToastNotification: React.FC<{
  toastImage: string
  productHandle: string
  removeToastNotification: () => void
}> = ({ toastImage, productHandle, removeToastNotification }) => {
  const translations = useTranslation()
  return (
    <ToastNotification>
      <ToastImage
        src={toastImage}
        srcs={{ sm: toastImage, md: toastImage }}
        aspectRatios={null}
        alt={productHandle}
        widths={{ unit: 'px', sm: 368, md: 368 }}
        loading={'default'}
      />
      <ToastRight>
        <ToastDescription>
          <ErrorHeadline role='alert' aria-live='polite'>
            <Exclamation />
            {translations.favorites.toastErrorAdding}
          </ErrorHeadline>
          <TypeStyleBodySmall>{translations.favorites.toastTryAgain}</TypeStyleBodySmall>
        </ToastDescription>
      </ToastRight>
      <StyledIconButton
        aria-label={translations.trayModal.close}
        variant='black-on-white-no-border'
        onClick={() => removeToastNotification()}
      >
        <ScreenReaderOnly>{translations.magnolia.general.closeToast}</ScreenReaderOnly>
        <SelectMark aria-hidden='true' />
      </StyledIconButton>
    </ToastNotification>
  )
}

const FavoritesToastNotification: React.FC<{
  toastImage: string
  productHandle: string
  removeToastNotification: () => void
}> = ({ toastImage, productHandle, removeToastNotification }) => {
  const translations = useTranslation()
  return (
    <ToastNotification>
      <ToastImage
        src={toastImage}
        srcs={{ sm: toastImage, md: toastImage }}
        aspectRatios={null}
        alt={productHandle}
        widths={{ unit: 'px', sm: 368, md: 368 }}
        loading={'default'}
      />
      <ToastRight>
        <ToastDescription>
          <TypeStyleHeadlineExtraExtraSmall role='alert' aria-live='polite'>
            {translations.favorites.toastItemAdded}
          </TypeStyleHeadlineExtraExtraSmall>
          <TypeStyleBodySmall>
            <TextLink href='/favorites' as={NextLink}>
              {translations.favorites.toastLink}
            </TextLink>
          </TypeStyleBodySmall>
        </ToastDescription>
      </ToastRight>
      <StyledIconButton
        variant='black-on-white-no-border'
        onClick={() => removeToastNotification()}
        aria-label={translations.trayModal.close}
      >
        <ScreenReaderOnly>{translations.magnolia.general.closeToast}</ScreenReaderOnly>
        <SelectMark aria-hidden='true' />
      </StyledIconButton>
    </ToastNotification>
  )
}

export const FavoritesToastNotifications = () => {
  const {
    currentPromoBarAndNavHeightDesktop,
    currentPromoBarAndNavHeightMobile,
    setPromoBarIsVisible,
    promoBarIsVisible,
  } = NavContainer.useContainer()
  return (
    <>
      <PromoBarIntersection
        setPromoBarIsVisible={setPromoBarIsVisible}
        isVisible={promoBarIsVisible}
      />
      <DialogProvider>
        <FavoritesToastNotificationsContent
          pushedDownDesktop={currentPromoBarAndNavHeightDesktop}
          pushedDownMobile={currentPromoBarAndNavHeightMobile}
        />
      </DialogProvider>
    </>
  )
}

export const RSCFavoritesToastNotifications = () => {
  const { currentPromoBarAndNavHeightDesktop, currentPromoBarAndNavHeightMobile } = useNavHeader()
  return (
    <DialogProvider>
      <FavoritesToastNotificationsContent
        pushedDownDesktop={currentPromoBarAndNavHeightDesktop}
        pushedDownMobile={currentPromoBarAndNavHeightMobile}
      />
    </DialogProvider>
  )
}

const FavoritesToastNotificationsContent = ({
  pushedDownDesktop,
  pushedDownMobile,
}: {
  pushedDownDesktop: number
  pushedDownMobile: number
}) => {
  const dialogContext = useDialogContext()
  const [toastNotification, setToastNotification] = useState<ToastNotificationEvent | null>(null)

  useCustomEventListener<ToastNotificationEvent>('open-toast-notification', event => {
    const customEventDetail = event.detail
    setToastNotification(customEventDetail)
    dialogContext?.show()
  })

  const removeToastNotification = () => {
    setToastNotification(null)
    dialogContext?.hide()
  }

  return toastNotification ? (
    <TransparentDialog
      modal={false}
      $pushedDownPixelsDesktop={pushedDownDesktop}
      $pushedDownPixelsMobile={pushedDownMobile}
      hideOnInteractOutside={false}
      autoFocusOnShow={false}
      unmountOnHide={false}
      autoFocusOnHide={false}
      preventBodyScroll={false}
    >
      <ToastNotificationWrapper
        key={
          toastNotification.type === 'favorites-add-toast-notification'
            ? toastNotification.payload.productHandle
            : undefined
        }
        onAnimationEnd={() => removeToastNotification()}
      >
        {toastNotification.type === 'favorites-add-toast-notification' ? (
          <FavoritesToastNotification
            productHandle={toastNotification.payload.productHandle}
            toastImage={toastNotification.payload.toastImage}
            removeToastNotification={removeToastNotification}
          />
        ) : (
          <FavoritesErrorToastNotification
            productHandle={toastNotification.payload.productHandle}
            toastImage={toastNotification.payload.toastImage}
            removeToastNotification={removeToastNotification}
          ></FavoritesErrorToastNotification>
        )}
      </ToastNotificationWrapper>
    </TransparentDialog>
  ) : null
}

const TransparentDialog = styled(Dialog)<{
  $pushedDownPixelsDesktop: number
  $pushedDownPixelsMobile: number
}>`
  position: fixed;
  top: calc(${o => o.theme.spacing(4)} + ${o => o.$pushedDownPixelsMobile}px);
  ${fromMd} {
    top: calc(${o => o.theme.spacing(6)} + ${o => o.$pushedDownPixelsDesktop}px);
  }
  right: 50%;
  z-index: ${({ theme }) => theme.zIndex.modals + 1};

  width: 650px;
  max-width: calc(100% - ${o => o.theme.spacing(8)});

  opacity: 0;
  transform: translateX(50%) translateY(50px) scale(0.99);

  &[data-enter] {
    opacity: 1;
    transform: translateX(50%) translateY(0px) scale(1);
  }

  transition:
    transform ${timingFast} ease-in-out,
    opacity ${timingFast} ease-in-out;
`

const ToastImage = styled(ResponsiveImage)`
  height: 88px;
  width: 88px;
  object-fit: cover;
  border-radius: 8px 0 0 8px;
`

const StyledIconButton = styled(IconButton)`
  padding: 0;
  width: 40px;
  height: 40px;
  margin: ${o => o.theme.spacing(2, 2, 0, 0)};
  align-self: flex-start;
`

const ToastDescription = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(2)};
`

const ToastRight = styled.div`
  display: flex;
  flex-grow: 1;
`

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`

const ToastNotification = styled.div`
  display: flex;
  box-shadow: 0px 5px 25px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  align-items: center;
  align-self: center;
  gap: ${o => o.theme.spacing(4)};
  background-color: ${o => o.theme.color.fill.background};
  &:hover,
  &:focus-within {
    animation-play-state: paused;
  }
  pointer-events: all;
`

const ToastNotificationWrapper = styled.div`
  width: 100%;
  animation: ${fadeOut} 5s forwards;
`

const PromoBarIntersection = ({
  setPromoBarIsVisible,
  isVisible,
}: {
  setPromoBarIsVisible: (x: boolean) => void
  isVisible: boolean
}) => {
  const promoBarRef = useIntersection(
    ([entry]) => {
      setPromoBarIsVisible(!!entry?.isIntersecting)
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: 0.01,
    }
  )
  return <FauxPromoBar ref={promoBarRef} $isVisible={isVisible} />
}

const FauxPromoBar = styled.div<{ $isVisible: boolean }>`
  min-width: 100%;
  min-height: ${promoBarHeightMobile};
  ${fromMd} {
    min-height: ${promoBarHeightDesktop};
  }
  position: absolute;
  top: 0;
  ${o => !o.$isVisible && `min-height: 0; max-height: 0; min-width: 0; max-width: 0;`}
`

const ErrorHeadline = styled(TypeStyleHeadlineExtraExtraSmall)`
  color: ${o => o.theme.color.stroke.critical.text.on.subtle};
  display: flex;
  gap: ${o => o.theme.spacing(2)};
  align-items: center;
`
